import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Building2, LayoutGrid, Users, ClipboardList, ScrollText, LogOut, Baby, Link2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

const NAV = [
  { to: "/platform", label: "Overzicht", icon: LayoutGrid, end: true },
  { to: "/platform/organisaties", label: "Organisaties", icon: Building2 },
  { to: "/platform/aanmeldlinks", label: "Aanmeldlinks", icon: Link2 },
  { to: "/platform/onboardingen", label: "Onboardingen", icon: ClipboardList },
  { to: "/platform/gebruikers", label: "Platformgebruikers", icon: Users },
  { to: "/platform/audit", label: "Supporthistorie", icon: ScrollText },
];

/** Frisse witte platform-shell — géén donker thema. */
export default function PlatformShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const initials = user?.name?.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase() || "P";

  return (
    <div className="min-h-screen flex bg-[color:var(--bone)] subtle-grain text-slate-900" data-testid="platform-shell">
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-neutral-200 bg-white flex-col">
        <div className="h-20 flex items-center gap-3 px-5 border-b border-neutral-200 shrink-0">
          <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "var(--sage)" }}>
            <Baby className="h-6 w-6 text-white" strokeWidth={2.4} />
          </div>
          <div className="min-w-0">
            <div className="font-display text-[15px] leading-tight text-neutral-900 truncate" data-testid="platform-brand">Webster</div>
            <div className="text-[10px] uppercase tracking-widest leading-tight mt-0.5" style={{ color: "var(--sage)" }}>
              Platform-beheer
            </div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="platform-nav">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`platform-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ` +
                (isActive
                  ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
                  : "text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900")
              }
            >
              <item.icon className="h-4 w-4" strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-neutral-200 text-[11px] text-neutral-500 shrink-0">
          Wet Kinderopvang · AVG · LRK · Webster
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 sm:h-16 border-b border-neutral-200 bg-white/90 backdrop-blur-sm flex items-center justify-between px-4 lg:px-6 gap-2" data-testid="platform-topbar">
          <div className="flex items-center gap-2 min-w-0">
            <Building2 className="h-4 w-4 text-neutral-500 shrink-0" />
            <span className="text-sm text-neutral-600 truncate">
              Platform-beheer <span className="text-neutral-400">·</span> <span className="font-medium text-neutral-900">alle KDV-organisaties</span>
            </span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="platform-user-menu" className="flex items-center gap-2 sm:gap-3 rounded-full p-1 sm:pr-3 hover:bg-neutral-100 transition-colors shrink-0">
                <Avatar className="h-8 w-8 border border-neutral-200">
                  <AvatarFallback className="text-xs font-semibold text-white" style={{ background: "var(--sage)" }}>{initials}</AvatarFallback>
                </Avatar>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-neutral-900 leading-tight">{user?.name}</div>
                  <div className="text-[11px] leading-tight" style={{ color: "var(--sage)" }}>Platform-superadmin</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>
                <div className="font-normal text-xs text-neutral-500">Aangemeld als</div>
                <div className="truncate">{user?.email}</div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="platform-logout-btn"
                onClick={async () => { await logout(); navigate("/login"); }}
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Uitloggen
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </header>
        <main className="flex-1 overflow-y-auto overflow-x-hidden p-4 sm:p-6 lg:p-8" data-testid="platform-main">
          {children}
        </main>
      </div>
    </div>
  );
}
